import { BidPaymentStatus } from "api";
import {
    settlementKey,
    settlementLeaves,
    settlementTotals,
    settlementReceived,
    settlementUpcomingInputs,
    settlementCarNumberMatches,
    settlementNdsTypeMatches,
} from "helpers/mutualSettlements";
import { calendarDayLabel, dateToView, momentDateInOffset } from "helpers/date";
import { ending } from "helpers/i18n";
import { formatValue, REPORT_TYPES } from "helpers/reports";
import moment from "moment";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { download, getItem } from "sources/books/reports";
import { setPaymentCheckedStatus } from "sources/books/settlementPayments";
import PaymentDiscipline from "./paymentDiscipline";
import PaymentHistory from "./paymentHistory";
import { BasisInfo, PaymentStatusBadge } from "./paymentDetails";
import SidePanel, { PanelStatus } from "./sidePanel";
import WithChangeDate from "./withChangeDate";

const upcomingPeriods = [3, 7, 14, 30];

const upcomingParams = (inputs, days) => {
    const today = moment().utcOffset(inputs.utcOffset).startOf("day");
    return {
        id: inputs.isAccountsPayable ? REPORT_TYPES.AccountsPayableReport : REPORT_TYPES.AccountsReceivableReport,
        source: inputs.source,
        startDate: today.format(),
        endDate: today
            .clone()
            .add(days - 1, "days")
            .endOf("day")
            .format(),
        counterpartyId: inputs.counterpartyId,
        carId: inputs.carId,
        paymentStatus: [BidPaymentStatus.NotPaid, BidPaymentStatus.PartiallyPaid],
        byPlanPaymentDate: true,
    };
};

/** Upcoming payments use their own period and unpaid statuses and inherit only identity filters. */
export default function SettlementPanels(props) {
    const [state, setState] = useState({
        days: 3,
        upcoming: [],
        dataKey: null,
        loading: true,
        error: "",
        errorKey: null,
        savingKey: null,
        saveError: "",
        downloading: false,
    });
    const update = useCallback(
        (patch) =>
            setState((previous) => ({
                ...previous,
                ...(typeof patch === "function" ? patch(previous) : patch),
            })),
        [],
    );
    const inputs = settlementUpcomingInputs(props);
    const requestKey = JSON.stringify([inputs, state.days]);
    const upcoming = state.dataKey === requestKey ? state.upcoming : [];
    const error = state.errorKey === requestKey ? state.error : "";
    const loading = state.loading || (state.dataKey !== requestKey && !error);
    const latest = useRef(null);
    const requestIdRef = useRef(0);
    const savingRef = useRef(false);
    const mounted = useRef(false);
    const previousOpenRequest = useRef(props.upcomingPanelRequest);
    useLayoutEffect(() => {
        latest.current = { props, inputs, state, requestKey };
    });

    const loadUpcoming = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        const current = latest.current;
        if (current.props.demoData) {
            update({ upcoming: [], dataKey: current.requestKey, loading: false, error: "" });
            return;
        }
        update({ loading: true, error: "", saveError: "" });
        try {
            const response = await getItem(upcomingParams(current.inputs, current.state.days));
            if (
                !mounted.current ||
                requestId !== requestIdRef.current ||
                current.requestKey !== latest.current.requestKey
            ) {
                return;
            }
            if (!response) {
                throw new Error("Не удалось загрузить предстоящие оплаты.");
            }
            const { legalPersonId, carFilter, ndsTypeIds } = current.inputs;
            const upcoming = settlementLeaves(response.data).filter(
                (row) =>
                    (!legalPersonId || row.legalPersonId === legalPersonId) &&
                    settlementNdsTypeMatches(row.ndsTypeId, ndsTypeIds) &&
                    settlementCarNumberMatches(row.carNumber, carFilter),
            );
            update({ upcoming, dataKey: current.requestKey, loading: false });
        } catch (error) {
            if (
                mounted.current &&
                requestId === requestIdRef.current &&
                current.requestKey === latest.current.requestKey
            ) {
                update({ error: error.message, errorKey: current.requestKey, loading: false });
            }
        }
    }, [update]);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            requestIdRef.current += 1;
        };
    }, []);
    useEffect(() => {
        loadUpcoming();
    }, [loadUpcoming, requestKey]);
    useEffect(() => {
        if (previousOpenRequest.current !== props.upcomingPanelRequest) {
            previousOpenRequest.current = props.upcomingPanelRequest;
            latest.current.props.openUpcoming();
        }
    }, [props.upcomingPanelRequest]);
    useEffect(() => {
        latest.current.props.onUpcomingInfoChange?.({
            count: upcoming.length,
            days: state.days,
            loading,
            error,
        });
    }, [upcoming.length, state.days, loading, error]);

    const check = async (row) => {
        if (savingRef.current || !props.canEdit) {
            return;
        }
        const checkedRequestKey = latest.current.requestKey;
        const refresh = props.refresh;
        savingRef.current = true;
        update({ savingKey: settlementKey(row), saveError: "" });
        try {
            await setPaymentCheckedStatus(row, props.isAccountsPayable, !row.isChecked);
            if (mounted.current && checkedRequestKey === latest.current.requestKey) {
                update((state) => ({
                    upcoming: state.upcoming.map((item) =>
                        settlementKey(item) === settlementKey(row) ? { ...item, isChecked: !row.isChecked } : item,
                    ),
                }));
            }
            await refresh();
        } catch (error) {
            if (mounted.current && checkedRequestKey === latest.current.requestKey) {
                update({ saveError: error.message });
            }
        } finally {
            savingRef.current = false;
            if (mounted.current) {
                update({ savingKey: null });
            }
        }
    };

    const downloadUpcoming = async () => {
        if (state.downloading) {
            return;
        }
        update({ downloading: true, saveError: "" });
        try {
            await download({
                ...upcomingParams(latest.current.inputs, latest.current.state.days),
                fileName: "Предстоящие оплаты.xlsx",
            });
        } catch {
            if (mounted.current) {
                update({ saveError: "Не удалось выгрузить список. Повторите запрос." });
            }
        } finally {
            if (mounted.current) {
                update({ downloading: false });
            }
        }
    };

    const renderUpcoming = () => {
        const { days, savingKey, saveError, downloading } = state;
        const awaitingData = (loading || !!error) && upcoming.length === 0;
        const offset = props.organizationTimezone.utcOffset;
        const start = momentDateInOffset(moment(), offset);
        const end = start.clone().add(days - 1, "days");
        const groups = upcoming.reduce((groups, row) => {
            const date = row.planPaymentDate ?? "";
            groups[date] = [...(groups[date] || []), row];
            return groups;
        }, {});
        return (
            <>
                <div className="settlements__toolbar d-flex flex-wrap align-items-end mb-3">
                    <div className="mr-auto">
                        <label className="small text-muted mb-1 d-block" htmlFor="upcoming-period">
                            Период
                        </label>
                        <select
                            id="upcoming-period"
                            className="form-control form-control-sm w-auto"
                            value={days}
                            onChange={(event) => update({ days: Number(event.target.value) })}
                        >
                            {upcomingPeriods.map((days) => (
                                <option key={days} value={days}>
                                    {days} {ending(days, "день", "дня", "дней")}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        disabled={loading}
                        onClick={loadUpcoming}
                    >
                        Обновить
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        disabled={loading || downloading || !upcoming.length}
                        onClick={downloadUpcoming}
                    >
                        {downloading ? "Выгружаем…" : "В Excel"}
                    </button>
                </div>
                <div className="settlements__upcoming-summary mb-3">
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <div className="font-weight-bold">Оснований: {awaitingData ? "—" : upcoming.length}</div>
                            <div className="small text-muted mt-1">
                                {dateToView(start.format("YYYY-MM-DD"))} — {dateToView(end.format("YYYY-MM-DD"))}
                            </div>
                        </div>
                        <div className="text-right ml-3">
                            <div className="small text-muted">К оплате</div>
                            <strong className="settlements__summary-value">
                                {awaitingData ? "—" : formatValue(settlementTotals(upcoming).pendingPayments, "double")}
                            </strong>
                        </div>
                    </div>
                </div>
                <PanelStatus loading={loading} error={error} retry={loadUpcoming} />
                {!loading && !error && !upcoming.length && (
                    <div className="settlements__empty">
                        <strong>Предстоящих оплат нет</strong>
                        <p className="small text-muted mb-0 mt-2">
                            За выбранный период с учётом фильтров отчёта. Выберите другой период или измените фильтры.
                        </p>
                    </div>
                )}
                {Object.keys(groups)
                    .sort()
                    .map((date) => (
                        <section key={date} className="settlements__date-group">
                            <h6 className="settlements__date-heading">
                                {date ? calendarDayLabel(date, offset) : "Без даты оплаты"}
                                <span className="text-muted font-weight-normal ml-2">{groups[date].length}</span>
                            </h6>
                            {groups[date].map((row) => (
                                <article key={settlementKey(row)} className="settlements__upcoming-item">
                                    <div className="d-flex align-items-start justify-content-between">
                                        <BasisInfo row={row} />
                                        <div className="settlements__amount text-right ml-3">
                                            <div className="small text-muted mb-1">К оплате</div>
                                            <strong>{formatValue(row.remainingAmount, "double")}</strong>
                                            <div className="mt-2">
                                                <PaymentStatusBadge
                                                    status={row.bidPaymentStatus}
                                                    overdue={row.isPaymentOverdue}
                                                    isAccountsPayable={props.isAccountsPayable}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="settlements__payment-context d-flex flex-wrap mt-3 small text-muted">
                                        <span>
                                            Плановая дата{" "}
                                            <b className="text-body">
                                                {row.planPaymentDate ? dateToView(row.planPaymentDate) : "—"}
                                            </b>
                                        </span>
                                        <span>
                                            Стоимость <b className="text-body">{formatValue(row.price, "double")}</b>
                                        </span>
                                        <span>
                                            {props.isAccountsPayable ? "Оплачено" : "Получено"}{" "}
                                            <b className="text-body">
                                                {formatValue(settlementReceived(row), "double")}
                                            </b>
                                        </span>
                                    </div>
                                    <div className="mt-3">
                                        <div className="small text-muted mb-1">Заметка</div>
                                        <WithChangeDate
                                            name="comment"
                                            commentPreview
                                            payment={row}
                                            isAccountsPayable={props.isAccountsPayable}
                                            disabled={!props.canEdit || !!savingKey}
                                            updateData={props.refresh}
                                        />
                                    </div>
                                    <label className="settlements__checked small d-flex align-items-center mt-2 mb-0">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={!!row.isChecked}
                                            disabled={!props.canEdit || !!savingKey}
                                            onChange={() => check(row)}
                                        />
                                        Проверено
                                        {savingKey === settlementKey(row) && (
                                            <span className="text-muted ml-2" role="status">
                                                Сохраняем…
                                            </span>
                                        )}
                                    </label>
                                </article>
                            ))}
                        </section>
                    ))}
                {saveError && (
                    <div className="alert alert-danger mt-3" role="alert">
                        {saveError}
                    </div>
                )}
            </>
        );
    };

    const { panel, demoData } = props;
    if (demoData) {
        return null;
    }
    return (
        <>
            {panel && (
                <SidePanel
                    title={
                        {
                            history: "История оплат",
                            upcoming: "Предстоящие оплаты",
                            discipline: "Платёжная дисциплина",
                        }[panel.type]
                    }
                    close={props.close}
                >
                    {panel.type === "history" && (
                        <PaymentHistory
                            row={panel.row}
                            isAccountsPayable={props.isAccountsPayable}
                            canEdit={props.canEdit}
                            revision={props.revision}
                            refresh={props.refresh}
                        />
                    )}
                    {panel.type === "upcoming" && renderUpcoming()}
                    {panel.type === "discipline" && (
                        <PaymentDiscipline
                            counterparty={panel.counterparty}
                            source={props.source}
                            revision={props.revision}
                        />
                    )}
                </SidePanel>
            )}
        </>
    );
}
